import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { lotService } from '../services/lotService';
import type { LotListItem, LotSelection } from '../types/lots.types';

interface Props {
  itemId: string;
  warehouseId: string;
  quantity: number;
  direction: 'IN' | 'OUT';
  value: LotSelection;
  onChange: (value: LotSelection) => void;
  allowMultiple?: boolean;
}

const LotPicker: React.FC<Props> = ({
  itemId, warehouseId, quantity, direction, value, onChange, allowMultiple = false,
}) => {
  const { t } = useTranslation('lots');
  const [lots, setLots] = useState<LotListItem[]>([]);
  const [warning, setWarning] = useState<string | null>(null);

  useEffect(() => {
    if (!itemId || direction !== 'OUT') return;
    lotService.byItem(itemId).then((rows) => {
      setLots(rows.filter((row) => row.status !== 'HOLD' && row.balances.some((b) => b.warehouseId === warehouseId && b.quantity > 0)));
    }).catch(() => setLots([]));
  }, [itemId, warehouseId, direction]);

  useEffect(() => {
    if (direction !== 'OUT' || value.mode !== 'auto' || !itemId || !warehouseId || quantity <= 0) {
      setWarning(null);
      return;
    }
    let cancelled = false;
    lotService.propose(itemId, warehouseId, quantity).then((allocation) => {
      if (cancelled) return;
      setWarning(allocation.warnings[0] || (allocation.unassigned > 0 ? `Lotu belirsiz: ${allocation.unassigned}` : null));
    }).catch(() => {
      if (!cancelled) setWarning(null);
    });
    return () => { cancelled = true; };
  }, [direction, value.mode, itemId, warehouseId, quantity]);

  if (direction === 'IN') {
    return (
      <Stack spacing={1} sx={{ mt: 1 }}>
        <TextField
          size="small"
          label={t('lotNo')}
          value={value.lotNo || ''}
          onChange={(e) => onChange({ ...value, mode: 'manual', lotNo: e.target.value })}
        />
        <Button size="small" onClick={() => onChange({ ...value, mode: 'manual', lotNo: '' })}>{t('newNumber')}</Button>
        <TextField size="small" type="date" label={t('expiryDate')} value={value.expiryDate || ''} onChange={(e) => onChange({ ...value, mode: 'manual', expiryDate: e.target.value })} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label={t('productionDate')} value={value.productionDate || ''} onChange={(e) => onChange({ ...value, mode: 'manual', productionDate: e.target.value })} InputLabelProps={{ shrink: true }} />
      </Stack>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={value.mode}
        onChange={(_, mode) => { if (mode) onChange({ ...value, mode, lots: [] }); }}
      >
        <ToggleButton value="auto">{t('auto')}</ToggleButton>
        <ToggleButton value="manual">{t('manual')}</ToggleButton>
      </ToggleButtonGroup>
      {warning && <Alert severity="info" sx={{ mt: 1 }}>{warning}</Alert>}
      {value.mode === 'manual' && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {(allowMultiple ? value.lots : value.lots.slice(0, 1)).concat(allowMultiple && value.lots.length === 0 ? [] : []).map((row, index) => (
            <Stack key={`${row.lotId}-${index}`} direction="row" spacing={1}>
              <TextField
                select
                size="small"
                label={t('lotNo')}
                value={row.lotId}
                onChange={(e) => {
                  const next = [...value.lots];
                  next[index] = { ...row, lotId: e.target.value };
                  onChange({ ...value, lots: next });
                }}
                sx={{ minWidth: 180 }}
              >
                {lots.map((lot) => <MenuItem key={lot.id} value={lot.id}>{lot.lotNo}</MenuItem>)}
              </TextField>
              <TextField
                size="small"
                type="number"
                label={t('quantity')}
                value={row.quantity}
                onChange={(e) => {
                  const next = [...value.lots];
                  next[index] = { ...row, quantity: Number(e.target.value) };
                  onChange({ ...value, lots: next });
                }}
                sx={{ width: 120 }}
              />
            </Stack>
          ))}
          {value.lots.length === 0 && (
            <TextField
              select
              size="small"
              label={t('lotNo')}
              value=""
              onChange={(e) => onChange({ ...value, lots: [{ lotId: e.target.value, quantity }] })}
            >
              {lots.map((lot) => <MenuItem key={lot.id} value={lot.id}>{lot.lotNo}</MenuItem>)}
            </TextField>
          )}
          {allowMultiple && value.lots.length > 0 && (
            <Button size="small" onClick={() => onChange({ ...value, lots: [...value.lots, { lotId: lots[0]?.id || '', quantity: 0 }] })}>+</Button>
          )}
          {lots.length === 0 && <Typography variant="caption">{t('empty')}</Typography>}
        </Stack>
      )}
    </Box>
  );
};

export default LotPicker;
